import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { IPropertySelected, ISelectedPlan } from "../interface"
import { FaXmark } from "react-icons/fa6";
import MultiInput from "components/input/multi";
import { Button } from "components/button";
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useSnackbarContext } from "components/snackbar";
import { useTranslation } from "react-i18next";
import TooltippedElement from "components/TooltippedElement"; 
import { IAppointmentTypes } from "../interface";
import { RiFileTextLine } from "react-icons/ri";
import { FaRedoAlt } from "react-icons/fa";
import ToggleSwitch from "components/toggleSwitch";
import DatePicker from "react-datepicker";
import { enUS, es } from "date-fns/locale";

interface IProps {
  selectedProperties: IPropertySelected[];
  setSelectedProperties: Dispatch<SetStateAction<IPropertySelected[]>>;
  step: string;
  setStep: Dispatch<SetStateAction<string>>;  
  selectedPlan: ISelectedPlan | null;
  setSelectedPlan: Dispatch<SetStateAction<ISelectedPlan | null>>;
  sending: boolean;
  setSending?: Dispatch<SetStateAction<boolean>>;
  sendProgress?: string;
  setSendProgress: Dispatch<SetStateAction<string>>;
  sendResults: {
    total: number;
    completed: number;
    successful: number;
    failed: number;
  };
  setSendResults: Dispatch<SetStateAction<{
    total: number;
    completed: number;
    successful: number;
    failed: number;
  }>>;
  className?: string;
};

const CHUNK_SIZE = 10;

const SelectPropertiesContainer = ({ 
  selectedProperties, 
  setSelectedProperties, 
  className, 
  step, 
  setStep, 
  selectedPlan, 
  setSelectedPlan, 
  sending,
  setSending, 
  setSendResults, 
  sendProgress, 
  setSendProgress, 
  sendResults 
}: IProps) => {
  const { t, i18n } = useTranslation();
  const tBase = "views.service-plan-dashboard.components.SelectPropertiesContainer";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const locale = i18n.language === "es" ? es : enUS;

  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarContext();

  const [dateOption, setDateOption] = useState("immediately");
  const [startDate, setStartDate] = useState<string>(new Date().toISOString());

  useEffect(() => {
    if (selectedPlan) {
      setDateOption(selectedPlan.start_date_type ?? "immediately");
    }
  }, [selectedPlan]);

  // const sendContractChunk = useMutation(
  //   async (info: {
  //     servicePlanId: string;
  //     startDate: string;
  //     properties: IPropertySelected[];
  //   }) => {
  //     const type = selectedPlan?.is_contract ? "send" : "add";
  //     await axios.post(
  //       `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/send-contract-bulk/${type}`, {     
  //         data: info 
  //       })
  //       .then((res) => res.data as { successful: number; failed: number; errors: string[] });
  //   }
  // );

  const sendContractChunk = useMutation(
    async (info: {
      servicePlanId: string;
      startDate: string;
      properties: IPropertySelected[];
    }): Promise<{ successful: number; failed: number }> => {
      const type = selectedPlan?.is_contract ? "send" : "add";
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/send-contract-bulk/${type}`, 
        { data: info }
      );
      return response.data;
    }
  );

    // Chunked send function
  const handleChunkedSend = async () => {
    if (!selectedPlan || selectedProperties.length === 0) return;

    setSending && setSending(true);
    setSendProgress("");
    
    // Filter out conflicting properties
    const propertiesToSend = selectedProperties.filter(p => !p.conflict);
    const totalProperties = propertiesToSend.length;
    
    // Initialize results
    const results = {
      total: totalProperties,
      completed: 0,
      successful: 0,
      failed: 0,
    };
    setSendResults(results);

    if (totalProperties === 0) {
      setSending && setSending(false);
      showSnackbar(tr("No properties to send"), "error");
      return;
    }

    setStep("success");

    // Split into chunks
    const chunks = [];
    for (let i = 0; i < propertiesToSend.length; i += CHUNK_SIZE) {
      chunks.push(propertiesToSend.slice(i, i + CHUNK_SIZE));
    }

    // Process chunks
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const chunkNumber = i + 1;
      const totalChunks = chunks.length;
      
      setSendProgress(`Processing batch ${chunkNumber} of ${totalChunks} (${chunk.length} properties)...`);
      
      try {
        const chunkResult = await sendContractChunk.mutateAsync({
          servicePlanId: selectedPlan.id,
          startDate,
          properties: chunk,
        });
        
        // Update counts based on detailed results
        results.successful += chunkResult.successful;
        results.failed += chunkResult.failed;
        
      } catch (error) {
        console.error(`Error in chunk ${chunkNumber}:`, error);
        // If the entire chunk failed, mark all as failed
        results.failed += chunk.length;
      } finally {
        // Update progress
        results.completed += chunk.length;
        setSendResults({...results});
        setSendProgress(`Completed ${results.completed} of ${totalProperties} properties...`);
      }
    }

    // Final results
    setSending && setSending(false);
    queryClient.invalidateQueries();
    setStep("success");
    
    if (results.successful > 0) {
      const isContract = selectedPlan?.is_contract;
      const message = isContract 
        ? `${results.successful} ${tr("contracts sent successfully")}` 
        : `${results.successful} ${tr("service plans added successfully")}`;
      
      if (results.failed > 0) {
        showSnackbar(`${message}. ${results.failed} failed.`, "warning");
      } else {
        showSnackbar(message, "success");
      }
    } else {
      showSnackbar(tr("All sends failed. Please check the errors."), "error");
    }
  };

  const active = step === "confirm-send";
  const isContract = selectedPlan?.is_contract;

  return (
    <div className={className + " w-full flex space-x-2 divide-x-2 border-b-2"}>
      <div className={`${active ? "w-1/3" : "w-full"} p-4`}>
        {/* Heading + warning message */}
        <div>
          {/* Selected Properties */}
          <div className="flex items-center justify-between border-b pb-2 gap-2">
            <div>
              {selectedProperties.length}{" "}
              {selectedProperties.length === 1
                ? tr("Property Selected")
                : tr("Properties Selected")}
            </div>
            {active && isContract && (
              <div className="text-gray-500 text-xs">
                ({tr("Emails are from the selected")} <br/> {tr("properties bill to customer")})
              </div>
            )}
            <button
              onClick={() => setSelectedProperties([])}
              className="text-primary"
            >
              {tr("Clear All")}
            </button>
          </div>
          {/* Warning message */}
          <div>
            {selectedPlan && selectedProperties.filter((property) => property.conflict).length >
              0 && (
              <div>
                <div className="text-sm text-red-500 mt-2 mb-1">
                  {
                    selectedProperties.filter((property) => property.conflict)
                      .length
                  }{" "}
                  {tr("Conflicting Properties")}
                </div>
                <div className="text-xs text-gray-500">
                  {tr("These properties have existing service plans. Please remove them or contact support.")}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected Properties */}
        <div className="flex flex-col gap-2 overflow-y-auto py-4">
          {selectedProperties.length > 0 &&
            selectedProperties.map((property) => (
              <div className=" " key={property.id}>
                <div
                  className={`flex p-2 items-center  rounded-full text-sm overflow-hidden divide-x
                  ${
                    property.conflict && selectedPlan
                      ? "bg-red-100 text-gray-800 divide-gray-400"
                      : "bg-blue-100 text-blue-800 divide-blue-400"
                  }
                `}
                >
                  <div className="flex-1 pl-2">
                    <div>{property.address}</div>
                    <div className="text-xs text-gray-500">
                      {property.city}, {property.state} {property.zipcode}
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setSelectedProperties((prev) =>
                        prev.filter((p) => p.id.toString() !== property.id)
                      )
                    }
                    className="w-[30px] flex justify-center items-center  py-1.5 !-mr-[1px] group rounded-r-full"
                    title="Remove"
                  >
                    <FaXmark
                      className={`${
                        property.conflict ? "text-gray-600" : "text-blue-600"
                      } group-hover:text-red-500`}
                      size={18}
                    />
                  </button>
                </div>
                {active && isContract && (
                  <div className="p-2">
                    <MultiInput
                      values={property.emails}
                      onChange={(email) => {
                        const emails = email.filter((e): e is string => !!e);
                        setSelectedProperties((prev) =>
                          prev.map((p) =>
                            p.id === property.id
                              ? { ...p, emails }
                              : p
                          )
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
      {active && (
        <>
          <div className="w-1/3 ">
            <div className="sticky top-0 p-4">
              <div className="flex items-center justify-between border-b pb-2 mb-4">
                <div>{tr("Selected Plan")}</div>
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="text-primary"
                >
                  {tr("Clear")}
                </button>
              </div>
              {/* Selected Plan */}
              {selectedPlan ? (
                <div className="border rounded-lg shadow-sm bg-white p-4 max-h-[calc(100vh_-_370px)] overflow-y-auto">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{selectedPlan.name}</h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="text-sm text-gray-500">
                          {selectedPlan.duration} {tr("months")}
                        </div>
                        {selectedPlan.auto_renew && (
                          <TooltippedElement 
                            message={tr("Auto Renew")}
                            placement="top"
                            element={ <FaRedoAlt size={20} className="text-blue-500" /> }
                          />
                        )}
                        {selectedPlan.is_contract && (
                          <TooltippedElement 
                            message={tr("Contract")}
                            placement="top"
                            element={ <RiFileTextLine size={20} className="text-primary" /> }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 overflow-y-auto">
                    {selectedPlan.description}
                  </p>
                  <div>
                    <h4 className="font-medium mb-2">
                      {tr("Appointment Types")}{" "}
                      <span>({selectedPlan.appointment_types.length})</span>
                    </h4>
                    <div className="flex overflow-x-auto gap-2 pb-2">
                      {selectedPlan.appointment_types.map((type: IAppointmentTypes) => (
                        <div
                          key={type.id}
                          className="flex justify-between items-center p-2 border rounded bg-gray-50"
                        >
                          <span className="whitespace-nowrap mr-2">{type.label}</span>
                          <span className="text-sm bg-gray-200 rounded-full px-2 py-1">
                            {type.service_plans_toapptypes.quantity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 mt-2 mb-1">
                  {tr("No plan selected")}
                </div>
              )}
            </div>
          </div>
          <div className="w-1/3">
            <div className="sticky top-0 p-4 flex flex-col space-y-10">
              <div>
                <div className="text-[18px] font-semibold">
                  {tr("Please confirm the selected addresses and service plan.")}
                </div>
              </div>
              <div className="pb-2">
                <div>
                 {tr("When would you like to start the service plan?")}
                </div>
                <ToggleSwitch
                  options={[
                    { value: "immediately", label: tr("Immediately") },
                    { value: "new_year", label: tr("January 1st"), tooltip: `${tr("January 1st")}, ${new Date().getFullYear()}` },
                    { value: "custom", label: tr("Custom") },
                  ]}
                  defaultValue={dateOption}
                  onChange={(value) => {
                    setDateOption(value);

                    if (value === "immediately") {
                      const immediately = new Date();
                      setStartDate(immediately.toISOString());
                    } else if (value === "new_year") {
                      const currentYear = new Date();
                      currentYear.setFullYear(currentYear.getFullYear());
                      currentYear.setMonth(0);
                      currentYear.setDate(1);
                      setStartDate(currentYear.toISOString());
                    } else if (value === "custom") {
                      // Don't reset the date here, just keep the last selected date
                      // This allows for better user experience when toggling back to custom
                    }
                  }}
                />
                {dateOption === "custom" && (
                  <div className="mt-2">
                    <DatePicker
                      className="h-10 w-full bg-white border border-gray-300 rounded-md shadow-sm text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 px-2"
                      selected={new Date(startDate)}
                      onChange={(date) => {
                        if (date && date instanceof Date) {
                          // Set time to noon to avoid timezone issues
                          date.setHours(12, 0, 0, 0);
                          setStartDate(date.toISOString());
                        }
                      }}
                      popperPlacement="bottom"
                      locale={locale}
                    />
                  </div>
                )}
              </div>

              {selectedPlan && selectedProperties.filter((property) => property.conflict).length > 0 && (
                <div>
                  <div className="text-sm mb-1">
                    {selectedPlan?.is_contract ? 
                    tr("Conflicting properties will not be sent.") :
                    tr("Conflicting properties will not be added.")}
                  </div>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => {
                        setSelectedProperties(prev => prev.filter(property => !property.conflict));
                      }}
                      className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-1 rounded border border-red-200 hover:border-red-300 bg-white hover:bg-red-50 transition-colors"
                      title={tr("Remove all conflicting properties")}
                    >
                      {tr("Remove Conflicts")}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-end border-t pt-2 pb-2">
                <Button
                  primary
                  disabled={sending || !selectedPlan || selectedProperties.length === 0}
                  onClick={handleChunkedSend}
                >
                  {selectedPlan?.is_contract ? tr("Send") : tr("Add Plans")}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectPropertiesContainer;